import React, { useMemo, useState } from 'react';
import {
  Paper,
  Text,
  Group,
  Button,
  NumberInput,
  Notification,
  Stack,
  RangeSlider,
  Textarea,
  Badge,
  Loader,
  ActionIcon,
} from '@mantine/core';
import { IconArrowRight, IconCheck, IconUpload, IconX } from '@tabler/icons-react';
import { deepPresetsClient } from '../../api/deepPresetsClient';

const theme = {
  background: {
    primary: '#0A0D14',
    secondary: '#161B22',
    tertiary: '#21262D',
    elevated: '#2D333B',
  },
  accent: {
    primary: '#4F46E5',
    secondary: '#7C3AED',
    tertiary: '#059669',
  },
  state: {
    success: '#10B981',
    error: '#EF4444',
    warning: '#F59E0B',
  },
  text: {
    primary: '#F9FAFB',
    secondary: '#9CA3AF',
    disabled: '#6B7280',
  },
  border: {
    primary: '#374151',
    secondary: '#4B5563',
  },
};

const modernStyles = {
  container: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${theme.background.primary} 0%, ${theme.background.secondary} 100%)`,
    color: theme.text.primary,
    padding: '12px',
    fontFamily: 'Space Grotesk, sans-serif',
  },
  card: {
    backgroundColor: theme.background.secondary,
    borderRadius: '12px',
    border: `1px solid ${theme.border.primary}`,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    backdropFilter: 'blur(12px)',
  },
};

const SOLANA_ADDRESS_REGEX = /(?<![A-Za-z0-9])[1-9A-HJ-NP-Za-km-z]{32,44}(?![A-Za-z0-9])/g;

const formatCurrency = (value: number) => {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(value >= 10_000 ? 0 : 1)}K`;
  }
  return value.toString();
};

const extractSolAddresses = (value: string) => {
  const matches = value.matchAll(SOLANA_ADDRESS_REGEX);
  return Array.from(new Set(Array.from(matches, (match) => match[0])));
};

const DevsMcapReportPage: React.FC = () => {
  const [devsInput, setDevsInput] = useState('');
  const [parsedAddresses, setParsedAddresses] = useState<string[]>([]);
  const [minPercent, setMinPercent] = useState<number | ''>(1);
  const [mcapRange, setMcapRange] = useState<[number, number]>([1000, 100_000_000]);
  const [mcapSliderPos, setMcapSliderPos] = useState<[number, number]>([0, 100]);
    // Non-linear mapping between slider position (0..100) and MCAP value (MCAP_MIN..MCAP_MAX)
    const MCAP_MIN = 1000;
    const MCAP_MAX = 100_000_000;

    const posToMcap = (pos: number) => {
      // exponential mapping gives higher granularity at the low end
      const min = MCAP_MIN;
      const max = MCAP_MAX;
      const v = Math.round(min * Math.pow(max / min, pos / 100));
      // snap to thousands for cleanliness
      return Math.round(v / 1000) * 1000;
    };

    const mcapToPos = (mcap: number) => {
      const min = MCAP_MIN;
      const max = MCAP_MAX;
      const ratio = Math.log(mcap / min) / Math.log(max / min);
      return Math.round(Math.max(0, Math.min(100, 100 * ratio)));
    };

    // Keep slider positions and mcapRange in sync
    React.useEffect(() => {
      setMcapSliderPos([mcapToPos(mcapRange[0]), mcapToPos(mcapRange[1])]);
    }, [mcapRange]);
  const [daysRange, setDaysRange] = useState<[number, number]>([0, 30]);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const parsedCount = useMemo(() => parsedAddresses.length, [parsedAddresses]);

  const handleDevsTextChange = (value: string) => {
    setDevsInput(value);
    const addresses = extractSolAddresses(value);
    setParsedAddresses(addresses);
    setError(null);
  };

  const parseFile = async (file: File) => {
    const text = await file.text();
    const addresses = extractSolAddresses(text);
    setDevsInput(addresses.join('\n'));
    setParsedAddresses(addresses);
    setError(null);
  };

  const handleFileInput = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await parseFile(file);
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      await parseFile(file);
    }
  };

  const handleSubmit = async () => {
    if (!parsedAddresses.length) {
      setError('Add at least one Solana wallet address.');
      return;
    }
    if (parsedAddresses.length > 1000) {
      setError('You can send up to 1000 addresses.');
      return;
    }
    if (minPercent === '' || Number(minPercent) < 0 || Number(minPercent) > 100) {
      setError('Min percent must be between 0 and 100.');
      return;
    }

    setError(null);
    setResult(null);
    setLoading(true);

    const now = Math.floor(Date.now() / 1000);
    const fromTs = now - Math.max(daysRange[1], 0) * 24 * 60 * 60;
    const tillTs = now - Math.min(daysRange[0], daysRange[1]) * 24 * 60 * 60;

    try {
      const res = await deepPresetsClient.exportDevsMcapReport({
        devs: parsedAddresses,
        min_percent: Number(minPercent),
        min_mcap: mcapRange[0],
        max_mcap: mcapRange[1],
        from_ts: fromTs,
        till_ts: tillTs,
      });
      const message = res?.message || res?.detail || 'Request sent successfully';
      setResult(message);
    } catch (e: any) {
      setResult(e?.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={modernStyles.container}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <Text
          size="24px"
          fw={700}
          mb={20}
          style={{
            background: `linear-gradient(135deg, ${theme.accent.primary} 0%, ${theme.accent.secondary} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center',
          }}
        >
          Devs MCAP report
        </Text>

        {error && (
          <Notification color="red" title="Validation error" mb="md" withCloseButton onClose={() => setError(null)}>
            {error}
          </Notification>
        )}

        {result && (
          <Notification
            color={result.toLowerCase().includes('fail') || result.toLowerCase().includes('error') ? 'red' : 'green'}
            title="Export status"
            mb="md"
            withCloseButton
            onClose={() => setResult(null)}
            icon={<IconCheck size={16} />}
          >
            {result}
          </Notification>
        )}

        <Paper p="md" mb="md" style={modernStyles.card}>
          <Stack gap="lg">
            <div>
              <Text size="sm" fw={600} mb={8} style={{ color: theme.text.secondary }}>
                Dev wallets
              </Text>
              <Text size="sm" c="dimmed" mb={12}>
                Paste addresses manually or upload a text/CSV file. The parser will extract Solana-like wallet addresses automatically.
              </Text>

              <div
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                style={{
                  border: `2px dashed ${isDragging ? theme.accent.primary : theme.border.primary}`,
                  borderRadius: 12,
                  padding: '16px',
                  background: isDragging ? 'rgba(79,70,229,0.08)' : theme.background.tertiary,
                  cursor: 'pointer',
                }}
              >
                <Group justify="space-between" align="center" mb={8}>
                  <Text size="sm" fw={600}>Drag & drop or select a file</Text>
                  <Button component="label" variant="outline" leftSection={<IconUpload size={16} />} size="sm">
                    Select file
                    <input type="file" accept=".txt,.csv,.json" hidden onChange={handleFileInput} />
                  </Button>
                </Group>
                <Text size="xs" c="dimmed">
                  Supported formats: plain text, CSV, JSON. The parser reads wallet addresses with a Solana-like pattern.
                </Text>
              </div>

              <Textarea
                mt="md"
                minRows={8}
                maxRows={16}
                value={devsInput}
                onChange={(event) => handleDevsTextChange(event.currentTarget.value)}
                placeholder="Paste addresses here, one per line or separated by commas"
                styles={{
                  input: {
                    backgroundColor: theme.background.tertiary,
                    border: `1px solid ${theme.border.primary}`,
                    color: theme.text.primary,
                  },
                }}
              />

              <Group mt="xs" gap="xs">
                <Badge color="blue" variant="light">
                  {parsedCount} addresses detected
                </Badge>
                {parsedCount > 0 && (
                  <ActionIcon variant="subtle" color="gray" onClick={() => {
                    setDevsInput('');
                    setParsedAddresses([]);
                  }}>
                    <IconX size={16} />
                  </ActionIcon>
                )}
              </Group>
            </div>

            <div>
              <Text size="sm" fw={600} mb={8} style={{ color: theme.text.secondary }}>
                Min percent
              </Text>
              <NumberInput
                min={0}
                max={100}
                step={0.1}
                value={minPercent}
                onChange={(value) => setMinPercent(typeof value === 'number' ? value : '')}
                suffix=" %"
                styles={{
                  input: {
                    backgroundColor: theme.background.tertiary,
                    border: `1px solid ${theme.border.primary}`,
                    color: theme.text.primary,
                  },
                }}
              />
            </div>

            <div style={{ marginBottom: 18 }}>
              <Text size="sm" fw={600} mb={8} style={{ color: theme.text.secondary }}>
                MCAP range
              </Text>
              <RangeSlider
                min={0}
                max={100}
                step={1}
                value={mcapSliderPos}
                onChange={(pos) => {
                  setMcapSliderPos(pos);
                  setMcapRange([posToMcap(pos[0]), posToMcap(pos[1])]);
                }}
                label={(pos) => formatCurrency(posToMcap(pos))}
                marks={[
                  { value: mcapToPos(1000), label: '' },
                  { value: mcapToPos(1_000_000), label: '1M' },
                  { value: mcapToPos(100_000_000), label: '' },
                ]}
                color={theme.accent.primary}
                styles={{
                  markLabel: { fontSize: 11, marginTop: 6, color: theme.text.secondary },
                  track: { height: 10 },
                  thumb: { width: 18, height: 18 },
                }}
              />
              <Group justify="space-between" mt={8}>
                <Text size="xs" c="dimmed">{formatCurrency(mcapRange[0])}</Text>
                <Text size="xs" c="dimmed">{formatCurrency(mcapRange[1])}</Text>
              </Group>
            </div>

            <div style={{ marginTop: 6 }}>
              <Text size="sm" fw={600} mb={8} style={{ color: theme.text.secondary }}>
                Date range
              </Text>
              <RangeSlider
                min={0}
                max={30}
                step={1}
                value={daysRange}
                onChange={setDaysRange}
                label={(value) => `${value}d`}
                marks={[
                  { value: 0, label: '' },
                  { value: 7, label: '7d' },
                  { value: 15, label: '15d' },
                  { value: 30, label: '' },
                ]}
                color={theme.accent.secondary}
                styles={{ markLabel: { fontSize: 11, marginTop: 6, color: theme.text.secondary } }}
              />
              <Group justify="space-between" mt={8}>
                <Text size="xs" c="dimmed">{daysRange[0]} day{daysRange[0] === 1 ? '' : 's'} ago</Text>
                <Text size="xs" c="dimmed">{daysRange[1]} day{daysRange[1] === 1 ? '' : 's'} ago</Text>
              </Group>
            </div>

            <Button
              fullWidth
              size="md"
              loading={loading}
              leftSection={loading ? <Loader size={16} /> : <IconArrowRight size={16} />}
              onClick={handleSubmit}
              style={{
                background: `linear-gradient(135deg, ${theme.accent.tertiary} 0%, #059669 100%)`,
                border: 'none',
                color: theme.text.primary,
                fontWeight: 600,
              }}
            >
              Run export
            </Button>
          </Stack>
        </Paper>
      </div>
    </div>
  );
};

export default DevsMcapReportPage;
